import React, { useState, useEffect } from 'react';
import { IconButton, Box, Typography, makeStyles } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import moment from 'moment';

interface MonthSliderProps {
    value: Date;
    onChange: (date: Date) => void;
    minDate?: Date;
    maxDate?: Date;
    format?: string;
    startOfMonth?: boolean;
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        width: 'fit-content',
    },
    monthText: {
        fontWeight: 500,
        minWidth: '120px',
        textAlign: 'center',
    },
    iconButton: {
        padding: theme.spacing(1),
    },
    disabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
}));

const MonthSlider: React.FC<MonthSliderProps> = ({
    value = new Date(),
    onChange,
    minDate,
    maxDate = new Date(),
    format = 'MMMM YYYY',
    startOfMonth = true,
}) => {
    const classes = useStyles();
    const [currentDate, setCurrentDate] = useState<Date>(value);

    useEffect(() => {
        setCurrentDate(value);
    }, [value]);

    const handlePrevious = () => {
        const newDate = moment(currentDate).subtract(1, 'month').toDate();
        if (!minDate || newDate >= minDate) {
            updateDate(newDate);
        }
    };

    const handleNext = () => {
        const newDate = moment(currentDate).add(1, 'month').toDate();
        if (newDate <= maxDate) {
            updateDate(newDate);
        }
    };

    const updateDate = (date: Date) => {
        const adjustedDate = startOfMonth
            ? moment(date).startOf('month').toDate()
            : date;
        setCurrentDate(adjustedDate);
        onChange(adjustedDate);
    };

    const isPreviousDisabled = minDate
        ? moment(currentDate).subtract(1, 'month').isBefore(minDate, 'month')
        : false;

    const isNextDisabled = moment(currentDate)
        .add(1, 'month')
        .isAfter(maxDate, 'month');

    return (
        <Box className={classes.root}>
            <IconButton
                className={`${classes.iconButton} ${
                    isPreviousDisabled ? classes.disabled : ''
                }`}
                onClick={handlePrevious}
                disabled={isPreviousDisabled}
                aria-label="previous month"
            >
                <ChevronLeft />
            </IconButton>

            <Typography variant="body1" className={classes.monthText}>
                {moment(currentDate).format(format)}
            </Typography>

            <IconButton
                className={`${classes.iconButton} ${
                    isNextDisabled ? classes.disabled : ''
                }`}
                onClick={handleNext}
                disabled={isNextDisabled}
                aria-label="next month"
            >
                <ChevronRight />
            </IconButton>
        </Box>
    );
};

export default MonthSlider;
